import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ArrowLeft, FolderOpen, Trash2, CheckSquare, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SortableRow, DragHandle, useDragReorder } from "@/components/DragTable";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

export default function GoalPage() {
  const { goalId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [goal, setGoal] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [taskStats, setTaskStats] = useState({ total: 0, done: 0 });

  const fetchData = useCallback(async () => {
    try {
      const [goalRes, projRes] = await Promise.all([
        API.get(`/goals/${goalId}`),
        API.get(`/goals/${goalId}/projects-with-stats`),
      ]);
      setGoal(goalRes.data);
      setProjects(projRes.data);
      setEditTitle(goalRes.data.title);
      setEditDesc(goalRes.data.description || "");
      // Compute total stats
      const total = projRes.data.reduce((s, p) => s + (p.task_count || 0), 0);
      const done = projRes.data.reduce((s, p) => s + (p.done_count || 0), 0);
      setTaskStats({ total, done });
    } catch {
      toast.error("Failed to load goal");
    }
  }, [goalId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateProject = async () => {
    if (!title.trim()) return;
    try {
      await API.post("/projects", { title, description, goal_id: goalId });
      toast.success("Project created");
      setTitle("");
      setDescription("");
      setShowCreate(false);
      fetchData();
    } catch {
      toast.error("Failed to create project");
    }
  };

  const handleUpdateGoal = async () => {
    try {
      await API.put(`/goals/${goalId}`, {
        title: editTitle,
        description: editDesc,
      });
      toast.success("Goal updated");
      setEditing(false);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const toggleComplete = async () => {
    if (goal.status !== "completed") {
      if (!(await confirm("Mark Goal Complete", "All projects and tasks under this goal will also be marked as complete."))) return;
    }
    await API.patch("/goals/" + goalId, { status: goal.status === "completed" ? "active" : "completed" });
    fetchData();
  };

  const handleDeleteGoal = async () => {
    if (!(await confirm("Delete Goal", "This will permanently delete this goal and all its projects and tasks.")))
      return;
    await API.delete(`/goals/${goalId}`);
    toast.success("Goal deleted");
    navigate("/");
  };

  const { sensors: projSensors, handleDragEnd: onProjectDragEnd } = useDragReorder(projects, (reordered) => setProjects(reordered), (reordered) => {
    API.put("/reorder/projects", { ids: reordered.map(p => p.project_id) }).catch(() => {});
  });
  const changeProjectStatus = async (pid, newStatus) => {
    if (newStatus === "completed") { if (!(await confirm("Mark Project Complete", "All tasks under this project will be marked as done."))) return; }
    await API.patch("/projects/" + pid, { status: newStatus });
    fetchData();
  };

  if (!goal)
    return <div className="text-[#787774]" data-testid="goal-loading">Loading...</div>;

  const progress = taskStats.total > 0 ? Math.round((taskStats.done / taskStats.total) * 100) : 0;

  return (
    <div data-testid="goal-page" className="max-w-[900px]">
      <button
        onClick={() => navigate("/")}
        className="flex items-center gap-1 text-sm text-[#787774] hover:text-[#2383E2] mb-6 transition-colors"
        data-testid="back-to-goals"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Goals
      </button>

      {/* Goal header card */}
      <div className="border border-[#E0E0DE] bg-[#F7F6F3]/40 rounded-sm p-6 mb-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {editing ? (
              <div className="space-y-3">
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="bg-transparent border-[#E0E0DE] text-2xl "
                  data-testid="edit-goal-title"
                />
                <Textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  className="bg-transparent border-[#E0E0DE] resize-none"
                  data-testid="edit-goal-description"
                />
                <div className="flex gap-2">
                  <Button onClick={handleUpdateGoal} className="bg-[#2383E2] text-white" size="sm" data-testid="save-goal-button">Save</Button>
                  <Button onClick={() => setEditing(false)} variant="ghost" size="sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <div>
                <h1 className="text-[28px] font-bold text-[#37352F]" data-testid="goal-title">
                  {goal.title}
                </h1>
                {goal.description && (
                  <p className="text-base text-[#787774] mt-2 leading-relaxed">{goal.description}</p>
                )}
              </div>
            )}
          </div>
          {!editing && (
            <div className="flex gap-1 ml-4">
              <Button onClick={() => setEditing(true)} variant="ghost" size="icon" className="text-[#787774]" data-testid="edit-goal-btn">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button onClick={handleDeleteGoal} variant="ghost" size="icon" className="text-[#EB5757]" data-testid="delete-goal-button">
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button onClick={toggleComplete} variant={goal.status === "completed" ? "default" : "outline"} size="sm" className={goal.status === "completed" ? "bg-[#2383E2] text-white text-[12px] h-7" : "border-[#E0E0DE] text-[#6B6B6B] text-[12px] h-7"} data-testid="toggle-goal-complete">
                {goal.status === "completed" ? "Completed" : "Mark Complete"}
              </Button>
            </div>
          )}
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-6 mt-5 pt-4 border-t border-[#E0E0DE]">
          <div className="flex items-center gap-1.5 text-sm text-[#787774]">
            <FolderOpen className="w-4 h-4" strokeWidth={1.5} />
            <span className="font-medium text-[#37352F]">{projects.length}</span> projects
          </div>
          <div className="flex items-center gap-1.5 text-sm text-[#787774]">
            <CheckSquare className="w-4 h-4" strokeWidth={1.5} />
            <span className="font-medium text-[#37352F]">{taskStats.done}</span>/{taskStats.total} tasks done
          </div>
          {taskStats.total > 0 && (
            <div className="flex items-center gap-2 flex-1">
              <div className="flex-1 h-1.5 bg-[#E0E0DE] rounded-full max-w-[200px]">
                <div className="h-full bg-[#2383E2] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-[#2383E2] font-medium">{progress}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Projects section */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-[#6B6B6B]">Projects</p>
        <Button onClick={() => setShowCreate(true)} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white" size="sm" data-testid="create-project-button">
          <Plus className="w-4 h-4 mr-1" /> New Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-16 border border-[#E0E0DE] border-dashed rounded-sm">
          <FolderOpen className="w-10 h-10 text-[#E0E0DE] mx-auto mb-3" strokeWidth={1} />
          <p className="text-[#787774] italic">No projects yet.</p>
        </div>
      ) : (
        <div className="border border-[#E0E0DE] rounded-[4px] overflow-auto">
          <Table>
            <colgroup><col style={{width:45}} /><col /><col style={{width:90}} /><col style={{width:90}} /><col style={{width:120}} /></colgroup>
            <TableHeader>
              <TableRow className="bg-[#F7F6F3] hover:bg-[#F7F6F3] border-b border-[#E0E0DE]">
                <TableHead className="w-12 text-[11px] font-medium text-[#6B6B6B]"></TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Project</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Status</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B] text-right">Tasks</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <DndContext sensors={projSensors} collisionDetection={closestCenter} onDragEnd={onProjectDragEnd}>
                <SortableContext items={projects.map(p => p.project_id)} strategy={verticalListSortingStrategy}>
                  {projects.map(proj => {
                    const pTaskCount = proj.task_count || 0;
                    const pDoneCount = proj.done_count || 0;
                    const pProgress = pTaskCount > 0 ? Math.round((pDoneCount / pTaskCount) * 100) : 0;
                    return (
                      <SortableRow key={proj.project_id} id={proj.project_id}>{({ dragProps }) => (<>
                        <TableCell><DragHandle dragProps={dragProps} /></TableCell>
                        <TableCell><span className={"text-[13px] font-medium cursor-pointer hover:underline " + (proj.status === "completed" ? "line-through text-[#A3A3A0]" : "text-[#37352F]")} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${proj.project_id}`); }}>{proj.title}</span>{proj.description && <span className="text-[12px] text-[#A3A3A0] ml-2 font-normal">{proj.description.slice(0,40)}</span>}</TableCell>
                        <TableCell onClick={e => e.stopPropagation()}><DropdownMenu><DropdownMenuTrigger asChild><button className={"text-[11px] px-2 py-0.5 rounded-[3px] font-medium cursor-pointer " + (proj.status === "completed" ? "bg-[#DBEDDB] text-[#1E4620]" : proj.status === "in_progress" ? "bg-[#D3E5EF] text-[#183B56]" : "bg-[#E8E8E8] text-[#555]")}>{proj.status === "completed" ? "Completed" : proj.status === "in_progress" ? "In Progress" : "Active"}</button></DropdownMenuTrigger><DropdownMenuContent className="bg-white border-[#E0E0DE] shadow-sm"><DropdownMenuItem onClick={() => changeProjectStatus(proj.project_id, "active")} className="text-[13px] cursor-pointer">Active</DropdownMenuItem><DropdownMenuItem onClick={() => changeProjectStatus(proj.project_id, "in_progress")} className="text-[13px] cursor-pointer">In Progress</DropdownMenuItem><DropdownMenuItem onClick={() => changeProjectStatus(proj.project_id, "completed")} className="text-[13px] cursor-pointer">Completed</DropdownMenuItem></DropdownMenuContent></DropdownMenu></TableCell>
                        <TableCell className="text-[13px] text-[#37352F] text-right">{pDoneCount}/{pTaskCount}</TableCell>
                        <TableCell>{pTaskCount > 0 ? <div className="flex items-center gap-2"><div className="flex-1 h-[4px] bg-[#E0E0DE] rounded-full"><div className="h-full bg-[#2383E2] rounded-full" style={{width:pProgress+"%"}} /></div><span className="text-[12px] text-[#6B6B6B]">{pProgress}%</span></div> : <span className="text-[12px] text-[#B8B6B0]">—</span>}</TableCell>
                      </>)}</SortableRow>
                    );
                  })}
                </SortableContext>
              </DndContext>
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#FFFFFF] border-[#E0E0DE]">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#37352F]">New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-transparent border-[#E0E0DE]" data-testid="project-title-input" />
            </div>
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 bg-transparent border-[#E0E0DE] resize-none" data-testid="project-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreateProject} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white" data-testid="submit-project-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
